import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()
const prefix = '/v2/end-users-dashboard'

// Schema definitions
const EndUsersPerCampaignSchema = z
  .object({
    id: z.string().optional(),
    campaignId: z.string(),
    campaignIncentiveId: z.string().nullable().optional(),
    referralOf: z.string().nullable().optional(),
    isInvited: z.boolean().optional(),
    questionnaireAnswers: z.any().optional(),
    happinessAnswers: z.number().nullable().optional(),
  })
  .passthrough()

const EndUsersPerCampaignTotalSchema = z.object({
  general: z.array(EndUsersPerCampaignSchema),
  referral: z.array(EndUsersPerCampaignSchema),
})

const IncentivesPerCampaignSchema = z.object({
  label: z.string(),
  data: z.number(),
})

const IncentivesPerCampaignByTypeSchema = z.object({
  general: z.array(IncentivesPerCampaignSchema),
  referral: z.array(IncentivesPerCampaignSchema),
})

const CampaignPerformanceSchema = z.object({
  action: z.string(),
  count: z.number(),
})

const CampaignPerformanceByTypeSchema = z.object({
  general: z.array(CampaignPerformanceSchema),
  referral: z.array(CampaignPerformanceSchema),
})

const CampaignPerformanceOverTimeDataSchema = z.object({
  label: z.string(),
  data: z.array(z.number()),
})

const CampaignPerformanceOverTimeSchema = z.object({
  labels: z.array(z.string()),
  data: z.array(CampaignPerformanceOverTimeDataSchema),
})

const CampaignPerformanceOverTimeByTypeSchema = z.object({
  general: CampaignPerformanceOverTimeSchema,
  referral: CampaignPerformanceOverTimeSchema,
})

const HappinessCheckCountSchema = z.object({
  happinessLevel: z.string().nullable(),
  count: z.number(),
})

const IncentiveItemVoucherCodeSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    status: z.string(),
  })
  .passthrough()

const GetVouchersInfoTypeSchema = z.object({
  incentiveItemId: z.string(),
  incentiveItemName: z.string().nullable().optional(),
  voucherCodes: z.array(IncentiveItemVoucherCodeSchema),
})

// Contract
export const endUsersContract = c.router({
  getEndUsersPerCampaign: {
    method: 'GET',
    path: `${prefix}/per-campaign/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: EndUsersPerCampaignTotalSchema,
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get end users per campaign',
  },
  getEndUsersHappinessCheck: {
    method: 'GET',
    path: `${prefix}/end-users/happiness-check/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: z.array(HappinessCheckCountSchema),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get happiness check results for a campaign',
  },
  getIncentivesPerCampaign: {
    method: 'GET',
    path: `${prefix}/end-users/incentives-per-campaign/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: IncentivesPerCampaignByTypeSchema,
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get incentives per campaign',
  },
  getVouchersInfo: {
    method: 'GET',
    path: `${prefix}/end-users/vouchers-info/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: z.array(GetVouchersInfoTypeSchema),
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get vouchers info for a campaign',
  },
  getCampaignPerformance: {
    method: 'GET',
    path: `${prefix}/end-users/campaign-performance/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: CampaignPerformanceByTypeSchema,
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get campaign performance',
  },
  getCampaignPerformanceOverTime: {
    method: 'GET',
    path: `${prefix}/end-users/campaign-performance-over-time/:campaignId`,
    pathParams: z.object({
      campaignId: z.string(),
    }),
    responses: {
      200: CampaignPerformanceOverTimeByTypeSchema,
      401: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get campaign performance over time',
  },
})
